import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { Fragment, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  So5LeaderboardSeasonality,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LineupsIcon } from '@sorare/core/src/atoms/icons/LineupsIcon';
import { SeasonalityIcon } from '@sorare/core/src/atoms/icons/SeasonalityIcon';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BodyL, BodyS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { LineupStyle } from 'hooks/usePreferredLineupStyle';
import { getAddTeamPath } from 'lib/so5';

import { AddTeamLayout } from '../AddTeamLayout';
import { EligibleCards } from '../EligibleCards';
import { AddTeam_so5LeaderboardGroupInterface } from './__generated__/index.graphql';

const AddTeamRoot = styled(Vertical).attrs({ center: true, gap: 4 })`
  width: 100%;
  padding: calc(6 * var(--unit)) var(--double-unit);
  justify-content: space-between;
  flex: 1;

  &.inline {
    padding: calc(3 * var(--unit)) var(--double-unit);
  }
`;

const MainContent = styled(Vertical)`
  flex: 1;
`;

const Counts = styled.div`
  display: grid;
  gap: var(--half-unit);
  grid-template-columns: auto repeat(4, 1fr);

  > * {
    justify-content: center;
  }
`;

const AddTeamWrapper = ({
  to,
  children,
  so5LeaderboardGroup,
  lineupStyle,
}: {
  to: string;
  children: ReactNode;
  so5LeaderboardGroup: AddTeam_so5LeaderboardGroupInterface;
  lineupStyle: LineupStyle;
}) => {
  return (
    <AddTeamLayout
      so5LeaderboardGroup={so5LeaderboardGroup}
      lineupStyle={lineupStyle}
    >
      <AddTeamRoot className={classNames(lineupStyle)}>
        <MainContent gap={2} center>
          <LineupsIcon size={4} />
          <BodyL bold>
            <FormattedMessage
              id="AddTeam.registerNewTeam"
              defaultMessage="Register a new team"
            />
          </BodyL>
          <LinkOverlay as={Link} to={to}>
            <Button asSpan color="secondary" size="small">
              <FormattedMessage {...fantasy.addTeam} />
            </Button>
          </LinkOverlay>
        </MainContent>
        {children}
      </AddTeamRoot>
    </AddTeamLayout>
  );
};

type Props = {
  so5LeaderboardGroupInterface: AddTeam_so5LeaderboardGroupInterface;
  lineupStyle?: LineupStyle;
};

export const AddTeam = ({
  so5LeaderboardGroupInterface,
  lineupStyle = 'spread',
}: Props) => {
  const { sport } = useSportContext();

  const to = so5LeaderboardGroupInterface
    ? getAddTeamPath({
        so5LeaderboardGroupInterface,
      })
    : '';

  const actualLeaderboardGroup = so5LeaderboardGroupInterface;

  const footballSeasonalities = actualLeaderboardGroup.seasonalities.length
    ? actualLeaderboardGroup.seasonalities
    : [So5LeaderboardSeasonality.ALL_SEASONS];
  const seasonalities =
    sport === Sport.FOOTBALL
      ? footballSeasonalities
      : actualLeaderboardGroup.seasonalities;

  if (to !== null) {
    return (
      <AddTeamWrapper
        so5LeaderboardGroup={actualLeaderboardGroup}
        to={to}
        lineupStyle={lineupStyle}
      >
        {sport === Sport.FOOTBALL && (
          <Vertical gap={1} center>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage {...fantasy.eligibleCards} />
            </BodyS>
            <Counts>
              {seasonalities.map(seasonality => (
                <Fragment key={seasonality}>
                  <SeasonalityIcon seasonality={seasonality} size={3} />
                  <EligibleCards
                    seasonality={seasonality}
                    so5LeaderboardGroupInterface={actualLeaderboardGroup}
                  />
                </Fragment>
              ))}
            </Counts>
          </Vertical>
        )}
      </AddTeamWrapper>
    );
  }

  return null;
};

AddTeam.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment AddTeam_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      displayName
      seasonalities
      so5Fixture {
        slug
        type
      }
      ...EligibleCards_so5LeaderboardGroupInterface
      ...getAddTeamPath_so5LeaderboardGroupInterface
      ...AddTeamLayout_so5LeaderboardGroupInterface
    }
    ${getAddTeamPath.fragments.so5LeaderboardGroupInterface}
    ${EligibleCards.fragments.so5LeaderboardGroupInterface}
    ${AddTeamLayout.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<AddTeam_so5LeaderboardGroupInterface>,
};
